import { useState, useMemo, useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useApolloClient, gql } from '@apollo/client';
import { get } from 'lodash';
import { message } from 'antd';

import { createPortal } from 'react-dom';
import { AnimatePresence } from 'motion/react';
import HeroSearch from './HeroSearch.jsx';
import OnboardingProgress from './OnboardingProgress.jsx';
import CareerProfileCard from './CareerProfileCard.jsx';
import ApplicationsPanel from './ApplicationsPanel.jsx';
import RecommendedJobs from './RecommendedJobs.jsx';
import InternalMobilityResources from './InternalMobilityResources.jsx';
import InterestedModal from './InterestedModal.jsx';
import { getAccentColor } from './utils.js';
import { parse, getEnvironment, lambda } from 'src/_shared/services/utils.js';
import { ONBOARDING_STEPS } from './constants.js';
import {
	UPDATE_INTERNAL_MOBILITY_IMAGE,
	QUERY_SELF_REFERRALS,
	QUERY_JOB_MATCHES_BY_USER,
} from './graphql.js';
import {
	updateUserCompany,
	updateCurrentUser,
	updateUserSearch,
} from '../_shared/user/userActions.js';
import { updateUser } from '../_shared/api/graphql/custom/users/updateUserGraphql.js';

function parseCareerProfile(raw) {
	try {
		return JSON.parse(raw || '{}') || {};
	} catch {
		return {};
	}
}

const CareerHubComponent = () => {
	const currentUser = useSelector((state) => state?.user?.currentUser);
	const allMultiLingualData = useSelector(
		(state) => state?.dashboard?.allMultiLingualData
	);
	const company = currentUser?.company;
	const dispatch = useDispatch();
	const client = useApolloClient();
	const history = useHistory();

	const accentColor = useMemo(() => getAccentColor(company), [company]);
	const [interestedMatch, setInterestedMatch] = useState(null);

	const savedCareerProfile = useMemo(
		() => parseCareerProfile(currentUser?.careerProfile),
		[currentUser?.careerProfile]
	);
	const savedOpenToRole = !!currentUser?.openToNewRole;

	const [draftOpenToRole, setDraftOpenToRole] = useState(savedOpenToRole);
	const [draftCareerProfile, setDraftCareerProfile] =
		useState(savedCareerProfile);
	const [isSaving, setIsSaving] = useState(false);
	const [activeTab, setActiveTab] = useState('nearby');
	const [selfReferrals, setSelfReferrals] = useState([]);
	const [referralsLoading, setReferralsLoading] = useState(true);
	const [jobMatchesByTab, setJobMatchesByTab] = useState({
		nearby: [],
		department: [],
		company: [],
	});
	const [jobsLoading, setJobsLoading] = useState(true);
	const [hideOpenToNewRoles, setHideOpenToNewRoles] = useState(false);

	const handleMobilitySettingsChange = useCallback((settings) => {
		if ('hideOpenToNewRoles' in settings) {
			setHideOpenToNewRoles(!!settings.hideOpenToNewRoles);
		}
	}, []);

	useEffect(() => {
		setDraftOpenToRole(savedOpenToRole);
	}, [savedOpenToRole]);

	useEffect(() => {
		setDraftCareerProfile(savedCareerProfile);
	}, [currentUser?.careerProfile]);

	useEffect(() => {
		if (!currentUser?.id) return;
		let cancelled = false;

		const fetchAllSelfReferrals = async () => {
			setReferralsLoading(true);
			const allItems = [];
			let nextToken = null;
			try {
				do {
					const { data } = await client.query({
						query: QUERY_SELF_REFERRALS,
						variables: {
							userId: currentUser.id,
							referralType: 'self',
							limit: 75,
							nextToken,
						},
						fetchPolicy: 'network-only',
					});
					const result = get(
						data,
						'queryReferralsByUserIdReferralTypeIndex',
						{}
					);
					allItems.push(...(result.items || []));
					nextToken = result.nextToken || null;
				} while (nextToken);

				if (!cancelled) {
					const withJob = allItems.filter((r) => r.job);
					withJob.sort(
						(a, b) => new Date(b.referralDate) - new Date(a.referralDate)
					);
					setSelfReferrals(withJob);
				}
			} catch (error) {
				console.error('Error fetching self referrals:', error);
			} finally {
				if (!cancelled) setReferralsLoading(false);
			}
		};

		fetchAllSelfReferrals();
		return () => {
			cancelled = true;
		};
	}, [client, currentUser?.id]);

	const refreshJobMatches = useCallback(async () => {
		if (!currentUser?.id || !currentUser?.companyId) return;
		setJobsLoading(true);
		const allItems = [];
		let nextToken = null;
		try {
			do {
				const { data } = await client.query({
					query: QUERY_JOB_MATCHES_BY_USER,
					variables: {
						userId: currentUser.id,
						first: 75,
						after: nextToken,
					},
					fetchPolicy: 'network-only',
				});
				const result = get(data, 'queryJobMatchesByUserIdIndex', {});
				allItems.push(...(result.items || []));
				nextToken = result.nextToken || null;
			} while (nextToken);

			const allowSelfReferralsInternalLink =
				currentUser?.company?.allowSelfReferralsInternalLink || false;
			const isExtendedUser = currentUser?.role === 'extendedUser';

			const filtered = allItems.filter((match) => {
				if (!match.active || !match.job) return false;
				if (match.job.companyId !== currentUser.companyId) return false;
				const hide = match.job.hideImInterested;
				if (hide === true || hide === 'true') return false;
				if (
					!isExtendedUser &&
					allowSelfReferralsInternalLink &&
					!match.job.internalJobLink
				) {
					return false;
				}
				return true;
			});

			filtered.sort((a, b) => (b.relevance ?? 0) - (a.relevance ?? 0));

			const processed = filtered.map((match) => ({
				...match,
				job: {
					...match.job,
					location: parse(match.job.location),
				},
			}));

			setJobMatchesByTab({
				nearby: processed.filter((m) => m.matchType === 'location'),
				department: processed.filter((m) => m.matchType === 'department'),
				company: processed.filter((m) => m.matchType === 'all'),
			});
		} catch (error) {
			console.error('Error fetching job matches:', error);
		} finally {
			setJobsLoading(false);
		}
	}, [client, currentUser?.id, currentUser?.companyId]);

	useEffect(() => {
		refreshJobMatches();
	}, [refreshJobMatches]);

	const appliedJobIds = useMemo(
		() => new Set(selfReferrals.map((r) => r.job?.id).filter(Boolean)),
		[selfReferrals]
	);

	const draftCareerProfileStr = useMemo(
		() => JSON.stringify(draftCareerProfile || {}),
		[draftCareerProfile]
	);
	const savedCareerProfileStr = useMemo(
		() => JSON.stringify(savedCareerProfile || {}),
		[savedCareerProfile]
	);

	const isDirty =
		draftOpenToRole !== savedOpenToRole ||
		draftCareerProfileStr !== savedCareerProfileStr;

	const onboardingSteps = useMemo(() => {
		const skillsCount = draftCareerProfile?.skills?.length || 0;
		const employmentCount = draftCareerProfile?.employment?.length || 0;
		const steps = hideOpenToNewRoles
			? ONBOARDING_STEPS.filter((s) => s.key !== 'open')
			: ONBOARDING_STEPS;
		return steps.map((step) => {
			switch (step.key) {
				case 'skills':
					return { ...step, complete: skillsCount > 0 };
				case 'experience':
					return { ...step, complete: employmentCount > 0 };
				case 'open':
					return { ...step, complete: draftOpenToRole };
				case 'apply':
					return { ...step, complete: selfReferrals.length > 0 };
				default:
					return step;
			}
		});
	}, [draftCareerProfile, draftOpenToRole, selfReferrals, hideOpenToNewRoles]);

	const jobs = jobMatchesByTab[activeTab] || [];

	const persistInternalMobilityImage = useCallback(
		async (newInternalMobilityImage) => {
			if (!company?.id) return;
			const result = await client.mutate({
				mutation: UPDATE_INTERNAL_MOBILITY_IMAGE,
				variables: {
					input: {
						id: company.id,
						internalMobilityImage: newInternalMobilityImage,
					},
				},
			});
			const updated = get(result, 'data.updateCompany');
			dispatch(
				updateUserCompany({
					...company,
					internalMobilityImage: updated?.internalMobilityImage ?? null,
				})
			);
		},
		[client, company, dispatch]
	);

	const handleUploadImage = useCallback(
		(newInternalMobilityImage) =>
			persistInternalMobilityImage(newInternalMobilityImage),
		[persistInternalMobilityImage]
	);

	const handleRemoveImage = useCallback(
		() => persistInternalMobilityImage(null),
		[persistInternalMobilityImage]
	);

	const handleUpdateProfile = useCallback(async () => {
		if (!currentUser?.id || !isDirty) return;
		const input = { id: currentUser.id };
		if (draftOpenToRole !== savedOpenToRole) {
			input.openToNewRole = draftOpenToRole;
		}
		if (draftCareerProfileStr !== savedCareerProfileStr) {
			input.careerProfile = draftCareerProfileStr;
		}

		try {
			setIsSaving(true);
			await client.mutate({
				mutation: gql(updateUser),
				variables: { input },
			});
			dispatch(updateCurrentUser({ ...currentUser, ...input }));
			message.success('Profile updated');

			const configMode = getEnvironment();
			await lambda({
				endpoint: `open-search-job-matching-api-${configMode}`,
				variables: { id: currentUser.id, configMode },
			});
			await refreshJobMatches();
		} catch (error) {
			console.error('Error updating profile:', error);
			message.error('Failed to update profile');
		} finally {
			setIsSaving(false);
		}
	}, [
		client,
		currentUser,
		dispatch,
		draftCareerProfileStr,
		draftOpenToRole,
		isDirty,
		refreshJobMatches,
		savedCareerProfileStr,
		savedOpenToRole,
	]);

	return (
		<div className="career-hub-root">
			<style>{`
				.content-area:has(> .career-hub-root) {
					padding-top: 0 !important;
					overflow-x: visible !important;
				}
				.col-12:has(> .content-area > .career-hub-root) {
					padding-left: 0 !important;
					padding-right: 0 !important;
				}
				.career-hub-root button:focus,
				.career-hub-root a:focus,
				.career-hub-root input:focus,
				.career-hub-root [role="switch"]:focus {
					outline: none !important;
					box-shadow: none !important;
				}
				@keyframes careerHubFadeIn {
					from { opacity: 0; transform: translateY(10px); }
					to { opacity: 1; transform: translateY(0); }
				}
				.career-hub-section {
					animation: careerHubFadeIn 0.4s ease-out both;
				}
			`}</style>

			<div className="career-hub-section" style={{ animationDelay: '0s' }}>
				<HeroSearch
					accentColor={accentColor}
					company={company}
					currentUser={currentUser}
					onUploadImage={handleUploadImage}
					onRemoveImage={handleRemoveImage}
					onSearch={(query) => {
						dispatch(updateUserSearch(query));
						history.push('/browsejobs');
					}}
				/>
			</div>

			<div
				className="mx-auto"
				style={{ maxWidth: 1280, padding: '24px 16px 16px' }}
			>
				<div className="flex flex-col gap-6">
					<InternalMobilityResources
						currentUser={currentUser}
						accentColor={accentColor}
						onSettingsChange={handleMobilitySettingsChange}
					/>

					<div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
						<div className="flex flex-col gap-6 lg:col-span-2">
							<div
								className="career-hub-section"
								style={{ animationDelay: '0.1s' }}
							>
								<OnboardingProgress
									steps={onboardingSteps}
									accentColor={accentColor}
								/>
							</div>

							<div
								className="career-hub-section"
								style={{ animationDelay: '0.2s' }}
							>
								<CareerProfileCard
									accentColor={accentColor}
									draftOpenToRole={draftOpenToRole}
									onChangeOpenToRole={setDraftOpenToRole}
									draftCareerProfile={draftCareerProfile}
									onChangeCareerProfile={setDraftCareerProfile}
									isDirty={isDirty}
									isSaving={isSaving}
									onUpdateProfile={handleUpdateProfile}
									hideOpenToNewRoles={hideOpenToNewRoles}
								/>
							</div>

							<div
								className="career-hub-section"
								style={{ animationDelay: '0.35s' }}
							>
								<RecommendedJobs
									activeTab={activeTab}
									onTabChange={setActiveTab}
									jobs={jobs}
									jobCounts={jobMatchesByTab}
									loading={jobsLoading}
									appliedJobIds={appliedJobIds}
									accentColor={accentColor}
									onInterested={(match) => setInterestedMatch(match)}
									onViewMore={() => history.push('/browsejobs')}
								/>
							</div>
						</div>

						<div className="lg:col-span-1">
							<div
								className="career-hub-section"
								style={{ animationDelay: '0.15s' }}
							>
								<ApplicationsPanel
									referrals={selfReferrals}
									loading={referralsLoading}
									accentColor={accentColor}
									onViewAll={() => history.push('/myapplications')}
								/>
							</div>
						</div>
					</div>
				</div>
			</div>

			{createPortal(
				<AnimatePresence>
					{interestedMatch && (
						<InterestedModal
							match={interestedMatch}
							currentUser={currentUser}
							allMultiLingualData={allMultiLingualData}
							accentColor={accentColor}
							onClose={() => setInterestedMatch(null)}
							onApplied={(jobId) => {
								setSelfReferrals((prev) => [
									{
										id: `new-${jobId}`,
										job: { id: jobId },
										referralDate: new Date().toISOString(),
										status: 'accepted',
									},
									...prev,
								]);
							}}
						/>
					)}
				</AnimatePresence>,
				document.body
			)}
		</div>
	);
};

export default CareerHubComponent;
